import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { Conversation, Cursor, Page } from '@cyphex/types';
import { chatApi } from '../api';
import { CHAT_KEYS, CONVERSATION_PAGE_SIZE } from '../constants';
import type { ChatFilter } from '../types';

export interface ConversationSection {
  id: 'self' | 'pinned' | 'favorites' | 'recent';
  title: string;
  items: Conversation[];
}

interface UseConversationsResult {
  sections: ConversationSection[];
  flat: Conversation[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

/**
 * Keyset-paginated DM list, grouped into the sections the sidebar renders.
 *
 * `selfChat` is passed in rather than fetched here because it comes from a
 * different endpoint (`/conversations/self`, get-or-create) and must be pinned
 * above everything regardless of the active filter or how far the list has
 * paged. Merging it at the section layer keeps the keyset paging untouched —
 * adding a leading sort key server-side would have broken the cursor.
 */
export function useConversations(
  filter: ChatFilter,
  search: string,
  selfChat: Conversation | null = null,
): UseConversationsResult {
  const query = useInfiniteQuery<Page<Conversation>, Error, Page<Conversation>[], readonly unknown[], Cursor | undefined>({
    queryKey: CHAT_KEYS.conversations(filter, search),
    initialPageParam: undefined,
    queryFn: ({ pageParam }) =>
      chatApi.listConversations({ filter, query: search || undefined }, pageParam),
    getNextPageParam: (lastPage) =>
      lastPage.pageInfo.hasNextPage ? (lastPage.pageInfo.endCursor ?? undefined) : undefined,
    select: (data) => data.pages,
    staleTime: 15_000,
  });

  const flat = useMemo(
    () => (query.data ?? []).flatMap((page) => page.items).slice(0, CONVERSATION_PAGE_SIZE * 10),
    [query.data],
  );

  const sections = useMemo<ConversationSection[]>(() => {
    // The self chat also arrives in the paged list (it is a pinned DM), so it
    // is removed there to avoid rendering the same row twice.
    const rest = selfChat === null ? flat : flat.filter((row) => row.id !== selfChat.id);
    const pinned = rest.filter((conversation) => conversation.self.isPinned);
    const favorites = rest.filter(
      (conversation) => !conversation.self.isPinned && conversation.self.isFavorite,
    );
    const recent = rest.filter(
      (conversation) => !conversation.self.isPinned && !conversation.self.isFavorite,
    );
    const all: ConversationSection[] = [
      ...(selfChat === null
        ? []
        : [{ id: 'self' as const, title: 'You', items: [selfChat] }]),
      { id: 'pinned', title: 'Pinned', items: pinned },
      { id: 'favorites', title: 'Favourites', items: favorites },
      { id: 'recent', title: 'Recent', items: recent },
    ];
    return all.filter((section) => section.items.length > 0);
  }, [flat, selfChat]);

  return {
    sections,
    flat,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
}
